import type { Block, ContentAST, InlineContent, ListItem } from '@shared/ast'

/**
 * Collect the ciphertext glyphs the server attached to encrypted nodes.
 *
 * The server sends `glyphs` only on nodes the viewer is permitted to read — those
 * nodes keep their plaintext, and the glyphs are the alternate face the reader UI
 * toggles to. An empty result means nothing on this page is toggleable.
 */
export function collectEncryptedGlyphs(ast: ContentAST | null | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  if (!ast?.blocks) {
    return out
  }

  const fromInline = (content: InlineContent[] | undefined) => {
    for (const node of content ?? []) {
      if (node.type === 'encrypted' && node.glyphs) {
        out[node.id] = node.glyphs
      }
    }
  }

  const fromItems = (items: ListItem[] | undefined) => {
    for (const item of items ?? []) {
      fromInline(item.content)
      fromItems(item.children)
    }
  }

  for (const block of ast.blocks as Block[]) {
    if ('content' in block) {
      fromInline(block.content as InlineContent[])
    }
    if (block.type === 'list') {
      fromItems(block.items)
    }
  }

  return out
}

/**
 * Ids of encrypted nodes that no key opens — someone decides when they open, and until
 * then they stay shut.
 *
 * The server sends `sealed` in place of `character` to everyone but the author and the
 * character themselves: it says a passage is withheld without saying by whom. The
 * reader UI uses it to stay quiet — no key prompt for a passage no key opens.
 */
export function collectSealedEncryptionIds(ast: ContentAST | null | undefined): Set<string> {
  const out = new Set<string>()
  if (!ast?.blocks) {
    return out
  }

  const fromInline = (content: InlineContent[] | undefined) => {
    for (const node of content ?? []) {
      if (node.type === 'encrypted' && node.sealed) {
        out.add(node.id)
      }
    }
  }

  const fromItems = (items: ListItem[] | undefined) => {
    for (const item of items ?? []) {
      fromInline(item.content)
      fromItems(item.children)
    }
  }

  for (const block of ast.blocks as Block[]) {
    if ('content' in block) {
      fromInline(block.content as InlineContent[])
    }
    if (block.type === 'list') {
      fromItems(block.items)
    }
  }

  return out
}
